import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api.js';

const LS_ENABLED = 'wave-chat-notif';

// Глобальный фоновый нотификатор новых входящих сообщений чата.
// Живёт в Root, поэтому работает на любой странице, пока пользователь залогинен.
export default function useChatNotifications({ enabled, isChatRoute }) {
    const [notifOn, setNotifOn] = useState(
        () =>
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted' &&
            localStorage.getItem(LS_ENABLED) === '1'
    );
    const [unread, setUnread] = useState(0);

    const lastSeenRef = useRef(0);
    const initedRef = useRef(false);
    const audioCtxRef = useRef(null);
    const notifOnRef = useRef(notifOn);
    const isChatRouteRef = useRef(isChatRoute);
    useEffect(() => { notifOnRef.current = notifOn; }, [notifOn]);
    useEffect(() => { isChatRouteRef.current = isChatRoute; }, [isChatRoute]);

    const playChirp = useCallback(() => {
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            if (!audioCtxRef.current) audioCtxRef.current = new AC();
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') ctx.resume();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = 'sine';
            o.frequency.setValueAtTime(880, ctx.currentTime);
            o.frequency.setValueAtTime(1180, ctx.currentTime + 0.11);
            g.gain.setValueAtTime(0.0001, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.34);
            o.start();
            o.stop(ctx.currentTime + 0.35);
        } catch { /* игнор */ }
    }, []);

    const showPush = useCallback((title, body) => {
        try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const n = new Notification(title, { body, tag: 'wave-chat', renotify: true });
                n.onclick = () => { window.focus(); n.close(); };
            }
        } catch { /* игнор */ }
    }, []);

    // Включение по клику пользователя (жест нужен и для Notification, и для AudioContext).
    const enable = useCallback(() => {
        if (typeof Notification === 'undefined') return;
        Notification.requestPermission().then((p) => {
            const on = p === 'granted';
            setNotifOn(on);
            localStorage.setItem(LS_ENABLED, on ? '1' : '0');
            playChirp();
        });
    }, [playChirp]);

    const clearUnread = useCallback(() => setUnread(0), []);

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;

        const tick = async () => {
            try {
                const params = lastSeenRef.current ? { sinceId: lastSeenRef.current } : {};
                const r = await api.get('/api/chat/notifications', { params });
                if (cancelled) return;
                const latestId = Number(r.data?.latestId || 0);
                const items = Array.isArray(r.data?.items) ? r.data.items : [];

                // Первый ответ — базовая точка, без сигналов.
                if (!initedRef.current) {
                    initedRef.current = true;
                    lastSeenRef.current = latestId;
                    return;
                }

                if (items.length > 0) {
                    if (!isChatRouteRef.current) setUnread((u) => u + items.length);
                    if (notifOnRef.current) {
                        playChirp();
                        const newest = items[0];
                        // Гасим пуш только если оператор прямо сейчас смотрит ИМЕННО этот диалог.
                        let activeContact = '';
                        try { activeContact = localStorage.getItem('wave-chat-active') || ''; } catch { /* игнор */ }
                        const viewingThis = !document.hidden && isChatRouteRef.current && newest.contact === activeContact;
                        if (!viewingThis) {
                            // Имя из профиля WhatsApp читается лучше голого номера.
                            const from = newest.senderName || newest.contact;
                            const who = newest.partnerName
                                ? `${newest.partnerName} · ${from}`
                                : from;
                            showPush('Новое сообщение', `${who}: ${newest.text || ''}`);
                        }
                    }
                }

                if (latestId > lastSeenRef.current) lastSeenRef.current = latestId;
            } catch { /* сеть — игнор до следующего тика */ }
        };

        tick();
        const id = setInterval(tick, 7000);
        return () => { cancelled = true; clearInterval(id); };
    }, [enabled, playChirp, showPush]);

    return { notifOn, enable, unread, clearUnread };
}
